import { useEffect, useRef, useState, type FC } from 'react';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import { LuX, LuPlus, LuCheck, LuInfo, LuChevronLeft, LuArrowRight } from 'react-icons/lu';
import {
    RegrasIAService, TOM_VOZ_OPTIONS, OBJETIVO_PRINCIPAL_OPTIONS,
    type ConfigAgente,
} from '../services/RegrasIAService';
import { listarModelos, type ModeloAgente } from '../data/modelosDeAgente';
import './NovoAgenteModal.css';

const MAX_TEXTO = 2000;

const rotuloDe = (opcoes: readonly { value: number; label: string }[], valor: number) =>
    opcoes.find(o => o.value === valor)?.label ?? '';

/* Transbordo é um campo de texto só, com as condições separadas por ";" —
   é assim que a tela do agente já grava. Contar aqui é só para o cartão
   não precisar despejar o texto inteiro. */
const contarTransbordo = (texto: string) =>
    texto.split(';').map(t => t.trim()).filter(Boolean).length;

interface Props {
    aberta: boolean;
    onFechar: () => void;
    /** "Começar do zero": a página leva para /chat-rules/novo, como antes. */
    onComecarDoZero: () => void;
    /** Agente criado e já salvo — a página navega para a bancada dele. */
    onCriado: (agente: ConfigAgente) => void;
}

/**
 * NOVO AGENTE — galeria de modelos em dois passos.
 *
 * Desenho aprovado em 2026-09-12,
 * docs/designsystem/propostas/agentes-prontos.html.
 *
 * O porquê dos dois passos: um modelo preenche 7 dos 8 campos do agente.
 * Sobra "sobre a empresa", que é obrigatório e só a empresa sabe. O passo 2
 * cobra esse campo — é o que faz a pessoa sair daqui com um agente COMPLETO
 * e salvo, em vez de cair num formulário de cinco seções tendo que descobrir
 * sozinha qual campo falta. Sem ele, a modal seria enfeite.
 *
 * O passo 2 também mostra as regras por extenso de propósito: elas entram no
 * prompt e mudam a resposta ao cliente final. Instalar 4 regras invisíveis
 * trocaria um problema por outro.
 *
 * EXCEÇÃO CONSCIENTE à regra do estado-do-redesign §5 ("formulário abre em
 * painel lateral, não em modal centralizada"): aquela regra é do cadastro de
 * registro; aqui não há registro sendo aberto, é a escolha de um ponto de
 * partida — e a galeria de 3 colunas não cabe nos 460px do painel. Decidido
 * por ele em 12/09. Não usar esta modal como precedente para cadastro.
 *
 * Sem hover — regra do produto. A seleção do cartão é borda + anel, e o foco
 * de teclado tem marca própria: sem hover, é ele que guia quem navega por Tab.
 */
const NovoAgenteModal: FC<Props> = ({ aberta, onFechar, onComecarDoZero, onCriado }) => {
    const [passo, setPasso] = useState<1 | 2>(1);
    const [modeloId, setModeloId] = useState<string | null>(null);
    const [nome, setNome] = useState('');
    const [sobreEmpresa, setSobreEmpresa] = useState('');
    const [criando, setCriando] = useState(false);
    /* Só depois de tentar criar é que o campo vazio vira erro visível — avisar
       antes de a pessoa ter chance de escrever seria ralhar sem motivo. */
    const [tentouCriar, setTentouCriar] = useState(false);

    const modelos = listarModelos();
    const modelo = modelos.find(m => m.id === modeloId) ?? null;

    const refSobre = useRef<HTMLTextAreaElement | null>(null);
    const refPrimeiroCartao = useRef<HTMLButtonElement | null>(null);

    /* Estado limpo a cada abertura: a modal anterior pode ter ficado no passo 2
       de um modelo que não interessa mais. */
    useEffect(() => {
        if (!aberta) return;
        setPasso(1);
        setModeloId(null);
        setNome('');
        setSobreEmpresa('');
        setCriando(false);
        setTentouCriar(false);
        requestAnimationFrame(() => refPrimeiroCartao.current?.focus());
    }, [aberta]);

    /* Esc no passo 1 fecha; no passo 2 VOLTA, preservando o que foi digitado.
       Fechar direto apagaria o "sobre a empresa" que a pessoa acabou de
       escrever, que é o campo mais caro da tela. */
    useEffect(() => {
        if (!aberta) return;
        const aoTeclar = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || criando) return;
            if (passo === 2) setPasso(1);
            else onFechar();
        };
        document.addEventListener('keydown', aoTeclar);
        return () => document.removeEventListener('keydown', aoTeclar);
    }, [aberta, passo, criando, onFechar]);

    useEffect(() => {
        if (passo === 2) requestAnimationFrame(() => refSobre.current?.focus());
    }, [passo]);

    if (!aberta) return null;

    const escolher = (m: ModeloAgente) => {
        setModeloId(m.id);
        /* Trocar de modelo não pode apagar um nome digitado à mão — só
           substitui enquanto o nome ainda for o sugerido pelo modelo anterior
           (ou estiver vazio). */
        setNome(atual => {
            const eraSugestao = !atual.trim() || modelos.some(o => o.nomeSugerido === atual);
            return eraSugestao ? m.nomeSugerido : atual;
        });
    };

    /* O botão NÃO desliga por campo vazio: botão apagado não explica o que
       falta, e "sobre a empresa" é justamente o campo que a pessoa não sabe
       que precisa preencher. Clicar com ele vazio marca o erro e leva o foco
       até lá — a API recusaria de qualquer forma, então é melhor dizer aqui. */
    const criar = async () => {
        setTentouCriar(true);
        if (!modelo) return;
        if (!nome.trim()) { document.getElementById('na-nome')?.focus(); return; }
        if (!sobreEmpresa.trim()) { refSobre.current?.focus(); return; }

        setCriando(true);
        try {
            const resposta = await RegrasIAService.createConfigAgente({
                nomeAgente: nome.trim(),
                tomVoz: modelo.tomVoz,
                objetivoPrincipal: modelo.objetivoPrincipal,
                sobreEmpresa: sobreEmpresa.trim(),
                instrucoesAdicionais: modelo.instrucoesAdicionais,
                condicoesTransbordo: modelo.condicoesTransbordo,
                // As regras vão ANINHADAS: a API cria as duas coisas numa
                // chamada só e atribui o id do agente novo a cada regra.
                regras: modelo.regras.map((texto, i) => ({ descricaoRegra: texto, ordem: i + 1 })),
                // Nunca FAQ: pergunta sem resposta viraria "R:" vazio no prompt.
                faqs: [],
                origemModelo: modelo.id,
            });

            if (resposta.sucesso && resposta.dados) {
                aviso.ok('Agente criado. Ele está pausado — teste antes de ligar.');
                onCriado(resposta.dados);
            } else {
                // A modal FICA ABERTA: o rascunho tem que sobreviver ao erro.
                aviso.erro(resposta.mensagem || 'Não foi possível criar o agente.');
            }
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setCriando(false);
        }
    };

    const faltaSobre = tentouCriar && !sobreEmpresa.trim();
    const faltaNome = tentouCriar && !nome.trim();

    return (
        <div
            className="na-overlay"
            /* Clique fora fecha só no passo 1. No passo 2 não faz nada — perder
               o texto escrito por um clique fora seria cruel. */
            onClick={() => { if (passo === 1 && !criando) onFechar(); }}
        >
            <div
                className="na"
                role="dialog"
                aria-modal="true"
                aria-label={passo === 1 ? 'Novo agente: escolher um modelo' : `Novo agente: ${modelo?.nome ?? ''}`}
                onClick={e => e.stopPropagation()}
            >
                <header className="na-head">
                    <div className="na-head-txt">
                        <h2 className="na-tit">{passo === 1 ? 'Novo agente' : modelo?.nome}</h2>
                        <p className="na-sub">
                            {passo === 1
                                ? 'Escolha um ponto de partida. Tudo continua editável depois.'
                                : 'Confira o que vem pronto e conte à IA sobre a sua empresa.'}
                        </p>
                    </div>
                    <button type="button" className="na-x" onClick={onFechar} aria-label="Fechar" disabled={criando}>
                        <LuX size={16} />
                    </button>
                </header>

                {passo === 1 ? (
                    <div className="na-corpo">
                        <div className="na-gal" role="radiogroup" aria-label="Modelos de agente">
                            {modelos.map((m, i) => {
                                const Icone = m.icone;
                                const escolhido = modeloId === m.id;
                                return (
                                    <button
                                        key={m.id}
                                        type="button"
                                        role="radio"
                                        aria-checked={escolhido}
                                        ref={i === 0 ? refPrimeiroCartao : undefined}
                                        className={`na-card ${escolhido ? 'is-on' : ''}`}
                                        onClick={() => escolher(m)}
                                        onDoubleClick={() => { escolher(m); setPasso(2); }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') { escolher(m); setPasso(2); }
                                        }}
                                    >
                                        <span className="na-card-tit"><Icone size={16} /> {m.nome}</span>
                                        <span className="na-card-txt">{m.resumo}</span>
                                        {/* Duas fileiras FIXAS, tom em cima e contagens embaixo. Numa
                                            fileira só, "Focado em conversão" quebra a linha em um
                                            cartão e estica a fileira inteira do grid — medido: 154px
                                            contra 129px, ou seja 25px de vão branco nos outros cinco.
                                            Com duas fileiras todo cartão tem a mesma altura e nenhuma
                                            delas fica vazia. */}
                                        <span className="na-card-anat">
                                            <span className="na-card-chips">
                                                <span className="na-chip is-tom">{rotuloDe(TOM_VOZ_OPTIONS, m.tomVoz)}</span>
                                            </span>
                                            <span className="na-card-chips">
                                                <span className="na-chip"><b>{m.regras.length}</b> regras</span>
                                                <span className="na-chip"><b>{m.perguntasSugeridas.length}</b> perguntas</span>
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}

                            {/* A porta antiga continua à vista, tracejada: quem já
                                sabe o que escrever não precisa de modelo. */}
                            <button type="button" className="na-card is-zero" onClick={onComecarDoZero}>
                                <span className="na-card-tit"><LuPlus size={16} /> Começar do zero</span>
                                <span className="na-card-txt">
                                    O formulário em branco, como é hoje. Para quem já sabe exatamente o que quer escrever.
                                </span>
                            </button>
                        </div>
                    </div>
                ) : modelo && (
                    <div className="na-corpo">
                        <div className="na-p2">
                            <div className="na-p2-esq">
                                <span className="na-rot">O que vem pronto</span>
                                <div className="na-linhas">
                                    <div className="na-linha">
                                        <span className="na-k">Tom de voz</span>
                                        <span>{rotuloDe(TOM_VOZ_OPTIONS, modelo.tomVoz)}</span>
                                    </div>
                                    <div className="na-linha">
                                        <span className="na-k">Objetivo</span>
                                        <span>{rotuloDe(OBJETIVO_PRINCIPAL_OPTIONS, modelo.objetivoPrincipal)}</span>
                                    </div>
                                    <div className="na-linha">
                                        <span className="na-k">Transbordo</span>
                                        <span>{contarTransbordo(modelo.condicoesTransbordo)} condições</span>
                                    </div>
                                </div>

                                <span className="na-rot">{modelo.regras.length} regras</span>
                                <div className="na-linhas">
                                    {modelo.regras.map(r => (
                                        <div className="na-linha is-regra" key={r}>
                                            <LuCheck size={13} /> <span>{r}</span>
                                        </div>
                                    ))}
                                </div>

                                <span className="na-rot">{modelo.perguntasSugeridas.length} perguntas sugeridas</span>
                                <div className="na-perguntas">
                                    {modelo.perguntasSugeridas.map(p => (
                                        <span className="na-pergunta" key={p}>{p}</span>
                                    ))}
                                </div>
                                <p className="na-nota">
                                    As perguntas ficam como sugestão na seção de Perguntas frequentes do agente —
                                    a resposta é sua, e a pergunta só entra no prompt depois que você responder.
                                </p>
                            </div>

                            <div className="na-p2-dir">
                                <div className="na-campo na-w-nome">
                                    <label className="na-lab" htmlFor="na-nome">Nome do assistente</label>
                                    <input
                                        id="na-nome"
                                        type="text"
                                        className={`na-inp ${faltaNome ? 'is-erro' : ''}`}
                                        value={nome}
                                        onChange={e => setNome(e.target.value)}
                                        disabled={criando}
                                    />
                                    <span className={`na-hint ${faltaNome ? 'is-erro' : ''}`}>
                                        {faltaNome
                                            ? 'O agente precisa de um nome.'
                                            : 'Sugerido pelo modelo. Use um nome próprio real.'}
                                    </span>
                                </div>

                                <div className="na-campo na-w-texto">
                                    <label className="na-lab" htmlFor="na-sobre">
                                        Sobre a empresa <i aria-hidden="true">*</i>
                                    </label>
                                    <textarea
                                        id="na-sobre"
                                        ref={refSobre}
                                        className={`na-ta ${faltaSobre ? 'is-erro' : ''}`}
                                        rows={5}
                                        maxLength={MAX_TEXTO}
                                        value={sobreEmpresa}
                                        onChange={e => setSobreEmpresa(e.target.value)}
                                        disabled={criando}
                                        placeholder="Somos uma loja de roupas fundada em 2015, com foco em moda casual e preços acessíveis. Atendemos toda a região de Curitiba com entrega em até 3 dias úteis."
                                    />
                                    <div className="na-ta-pe">
                                        <span className={`na-hint ${faltaSobre ? 'is-erro' : ''}`}>
                                            {faltaSobre
                                                ? 'Sem isto a IA não tem o que dizer sobre vocês.'
                                                : 'O único campo que o modelo não tem como saber. Diga o que vocês fazem, para quem e onde atendem.'}
                                        </span>
                                        <span className="na-conta">{sobreEmpresa.length} / {MAX_TEXTO}</span>
                                    </div>
                                </div>

                                <p className="na-aviso">
                                    <LuInfo size={13} />
                                    <span>
                                        O agente nasce <b>pausado</b>. Você conversa com ele no ensaio antes de colocar no ar.
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <footer className="na-pe">
                    <span className="na-passo">
                        {passo === 1 ? `Passo 1 de 2 · ${modelos.length} modelos` : 'Passo 2 de 2'}
                    </span>
                    {passo === 1 ? (
                        <>
                            <button type="button" className="na-btn" onClick={onFechar}>Cancelar</button>
                            <button
                                type="button"
                                className="na-btn is-primary"
                                disabled={!modelo}
                                onClick={() => setPasso(2)}
                            >
                                Continuar <LuArrowRight size={14} />
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" className="na-btn" onClick={() => setPasso(1)} disabled={criando}>
                                <LuChevronLeft size={14} /> Voltar
                            </button>
                            <button
                                type="button"
                                className="na-btn is-primary"
                                onClick={criar}
                                disabled={criando}
                            >
                                {criando ? 'Criando…' : 'Criar agente'}
                            </button>
                        </>
                    )}
                </footer>
            </div>
        </div>
    );
};

export default NovoAgenteModal;
